import { silentRefresh } from "@/api/refresh";
import { tokenStore } from "@/auth/tokenStore";
import { getProtocol } from "@/lib/protocol";
import { authIdentity } from "@/utils/token";
import axios from "axios";
import { canRefreshAuthentication } from "./authFailure";

import { API_URL } from "./endpoints";
export {
  ACCESS_MANAGER_BASE,
  API_URL,
  ASSET_BASE,
  AUDIT_BASE,
  EMISSION_BASE,
  MODULE_BASE,
  NOTIFICATION_BASE,
  ORGANISATION_BASE,
  PW_SERVER,
  SECURITY_BASE,
  SRM_BASE,
  SUBSCRIPTION_BASE,
  USER_BASE,
  VERIFY_BASE,
} from "./endpoints";

declare module "axios" {
  export interface AxiosRequestConfig {
    /** Skip automatic Authorization injection (public endpoints such as login). */
    skipAuth?: boolean;
    /** Internal: mark a request as a 401-refresh retry to avoid loops. */
    _refreshed?: boolean;
    _authIdentity?: string | null;
    /** Application session when dispatched; explicit pre-login tokens may differ. */
    _sessionIdentity?: string | null;
  }
}

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

export const withBearer = (token: string) =>
  /^Bearer\s/i.test(token) ? token : `Bearer ${token}`;

/** Build Authorization headers for raw fetch calls that bypass the axios interceptor. */
export const authHeaders = (
  token: string,
): { headers: { Authorization: string } } => ({
  headers: { Authorization: withBearer(token) },
});

// Access tokens stay in memory; only the HttpOnly refresh cookie survives reloads.
const currentToken = (): string | null => tokenStore.get();

// Single source of truth for request enrichment: Authorization + tenant context.
apiClient.interceptors.request.use((config) => {
  if (!config.skipAuth && config._sessionIdentity === undefined) {
    config._sessionIdentity = authIdentity(currentToken());
  }
  if (!config.skipAuth && !config.headers.Authorization) {
    const token = currentToken();
    if (token) {
      config.headers.Authorization = withBearer(token);
    }
  }
  if (!config.skipAuth && config._authIdentity === undefined) {
    const bearer = String(config.headers.Authorization || "").replace(
      /^Bearer\s+/i,
      "",
    );
    config._authIdentity = authIdentity(bearer || null);
  }
  if (config.params) {
    config.params.protocol = getProtocol();
  }
  // Tenant/user headers are sent by the QA gateway — kept commented out for reference.
  // try {
  //   const user = localStorage.getItem('user');
  //   if (user) {
  //     const parsed = JSON.parse(user) as Record<string, unknown>;
  //     if (parsed.userId) config.headers['userId'] = String(parsed.userId);
  //     if (parsed.email) config.headers['username'] = String(parsed.email);
  //     if (parsed.companyId && import.meta.env.DEV) config.headers['companyId'] = String(parsed.companyId);
  //   }
  // } catch {
  //   // Malformed user payload — ignore
  // }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (
      !response.config.skipAuth &&
      response.config._sessionIdentity !== authIdentity(currentToken())
    ) {
      throw new axios.CanceledError(
        "The authenticated session changed before the response arrived.",
      );
    }
    return response;
  },
  async (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      !error.config?.skipAuth
    ) {
      if (
        error.config?._refreshed ||
        !canRefreshAuthentication(error.response.data)
      ) {
        // Refresh cannot repair revocation, invalid claims or a rejected retry.
        // Stop protected polling before publishing any replacement token.
        if (error.config?._authIdentity === authIdentity(currentToken())) {
          window.dispatchEvent(new Event("auth:session-expired"));
        }
        return Promise.reject(error);
      }
    }
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      !error.config?.skipAuth &&
      !error.config?._refreshed
    ) {
      // Phase 1: attempt a single-flight silent refresh before giving up.
      if (error.config?._authIdentity !== authIdentity(currentToken())) {
        throw new axios.CanceledError(
          "The authenticated session changed before refresh.",
        );
      }
      // A concurrent request may have already refreshed this same session.
      const sentToken = String(
        error.config?.headers?.Authorization || "",
      ).replace(/^Bearer\s+/i, "");
      const current = currentToken();
      const fresh =
        current &&
        current !== sentToken &&
        authIdentity(current) === error.config?._authIdentity
          ? current
          : await silentRefresh();
      if (fresh) {
        if (error.config?._authIdentity !== authIdentity(fresh)) {
          throw new axios.CanceledError(
            "The authenticated session changed before the request could be retried.",
          );
        }
        const retryConfig = { ...error.config, _refreshed: true };
        if (retryConfig.headers) {
          retryConfig.headers.Authorization = withBearer(fresh);
        }
        return apiClient.request(retryConfig);
      }
    }
    if (axios.isAxiosError(error) && !error.config?.skipAuth) {
      const body = error.response?.data as
        { code?: string; error?: { code?: string } } | undefined;
      const code = body?.code || body?.error?.code;
      if (
        [
          "AUTH_SESSION_REVOKED",
          "AUTH_INVALID_CLAIMS",
          "AUTH_CREDENTIALS_CHANGED",
        ].includes(code || "") &&
        error.config?._authIdentity === authIdentity(currentToken())
      ) {
        window.dispatchEvent(new Event("auth:session-expired"));
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
